import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReminderConfigs,
  getReminderConfig,
  createReminderConfig,
  updateReminderConfig,
  deleteReminderConfig,
  getReminderHistory,
  sendManualReminder,
  processReminders,
  type ReminderConfig,
  type PaymentReminderHistory,
} from '@/services/reminderService';

/**
 * Hook to fetch all reminder configurations with optional filters
 */
export const useReminderConfigs = (query?: {
  trigger?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['reminderConfigs', query],
    queryFn: () => getReminderConfigs(query),
  });
};

/**
 * Hook to fetch a specific reminder configuration by ID
 */
export const useReminderConfig = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ['reminderConfig', id],
    queryFn: () => getReminderConfig(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to create a new reminder configuration
 */
export const useCreateReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createReminderConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminderConfigs'] });
    },
  });
};

/**
 * Hook to update an existing reminder configuration
 */
export const useUpdateReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ReminderConfig> }) =>
      updateReminderConfig(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reminderConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['reminderConfig', variables.id] });
    },
  });
};

/**
 * Hook to delete (deactivate) a reminder configuration
 */
export const useDeleteReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteReminderConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminderConfigs'] });
    },
  });
};

/**
 * Hook to fetch reminder history with filters
 */
export const useReminderHistory = (query?: {
  studentId?: string;
  status?: string;
  trigger?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['reminderHistory', query],
    queryFn: () => getReminderHistory(query),
  });
};

/**
 * Hook to manually send a reminder
 */
export const useSendManualReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendManualReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminderHistory'] });
    },
  });
};

/**
 * Hook to trigger manual reminder processing
 */
export const useProcessReminders = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processReminders,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminderHistory'] });
    },
  });
};
